/**
 * <carousel-progress-bar>
 *
 * Finds the nearest <slideshow-slides> scroller (walking up the DOM) and
 * updates its inner fill width as the carousel is scrolled. The fill grows
 * from 0% to 100% left-to-right: visible slides share / total slides share.
 *
 * When multiple scrollers exist in the same scope (e.g. desktop grid + mobile
 * carousel), the one not inside `.hidden--mobile` is preferred.
 */
class CarouselProgressBar extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {HTMLElement | null} */
  #fill = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  #onScroll = () => this.#update();

  connectedCallback() {
    this.#fill = this.querySelector('.carousel-progress-bar__fill');
    if (!this.#fill) return;

    if (!this.#bindScroller()) {
      // Carousel may not be in the DOM yet (sections rendered async, e.g. recommendations).
      this.#watchForScroller();
    }
  }

  disconnectedCallback() {
    this.#teardown();
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = null;
  }

  #teardown() {
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#scroller = null;
  }

  /**
   * Walks up ancestors to find a container that has a <slideshow-slides> descendant,
   * preferring the scroller not inside `.hidden--mobile`.
   * @returns {boolean} true when a scroller was found and bound.
   */
  #bindScroller() {
    /** @type {HTMLElement | null} */
    let el = this.parentElement;
    /** @type {HTMLElement | null} */
    let scroller = null;

    while (el) {
      const scrollers = /** @type {HTMLElement[]} */ ([...el.querySelectorAll('slideshow-slides')]);
      if (scrollers.length) {
        scroller = scrollers.find((s) => !s.closest('.hidden--mobile')) || scrollers[0] || null;
        break;
      }
      el = el.parentElement;
    }

    if (!scroller) return false;

    this.#scroller = scroller;
    scroller.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(scroller);
    requestAnimationFrame(() => this.#update());
    return true;
  }

  #watchForScroller() {
    // Observe the nearest ancestor with an id (section root), or the body as fallback.
    /** @type {HTMLElement | null} */
    let root = this.parentElement;
    while (root && !root.id) root = root.parentElement;
    root = root || document.body;
    this.#mutationObserver = new MutationObserver(() => {
      if (this.#bindScroller()) {
        this.#mutationObserver?.disconnect();
        this.#mutationObserver = null;
      }
    });
    this.#mutationObserver.observe(root, { childList: true, subtree: true });
  }

  #update() {
    if (!this.#scroller || !this.#fill) return;
    const { scrollWidth, clientWidth, scrollLeft } = this.#scroller;
    if (scrollWidth <= clientWidth + 1) {
      this.setAttribute('data-not-scrollable', '');
      return;
    }
    this.removeAttribute('data-not-scrollable');
    const ratio = (scrollLeft + clientWidth) / scrollWidth;
    const pct = Math.min(100, Math.max(0, ratio * 100));
    this.#fill.style.setProperty('--progress-fill-width', `${pct}%`);
  }
}

if (!customElements.get('carousel-progress-bar')) {
  customElements.define('carousel-progress-bar', CarouselProgressBar);
}
